import React from 'react'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Footer, SignUpPageStyled } from './SignUpPage.styled'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import type { Campaign } from '@mobi/account/Areas/SignUp'
import { useHistory } from '@mobi/account/Hooks/useHistory'
import { DEPOSIT_ROOT } from '@mobi/account/Areas/Deposit/Utils/routes'
import {
  getButtonText,
  getExpiryDate,
  getTermsAndConditions,
  getTitleText,
  shouldDisplayAmounts,
  shouldDisplayCampaign,
  shouldShowButton,
  getRemainingAmount,
  getTotalAmount,
} from '../Utils/BonusOffer'
import Lottie from 'react-lottie'
import LottieConfetti from '../assets/lottie-celebrations.json'
import {
  CampaignDescriptionStyled,
  BonusCardContainer,
  BonusCardHeader,
  BonusCardExpiry,
  BonusCardBody,
  BonusCardTotal,
  BonusCardRemaining,
  BonusCardTitle,
  BonusCardDescription,
} from './SignUpCompletePage.styled'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { spacing } from '@mobi/component-library/Foundations'
import { trackSignUpDepositClick, trackSignUpGoBackHomePage } from '../Utils/ga'
import { getCampaignRaceUrl } from '@mobi/account/Utils'

interface SignUpCompletePageProps {
  campaign: Campaign | undefined
  openDeposit: () => void
}

export const SignUpCompletePage = ({ campaign, openDeposit }: SignUpCompletePageProps) => {
  const appHistory = useHistory()
  const isCampaignActive = campaign && shouldDisplayCampaign(campaign)

  const handleCampaignClick = (campaign: Campaign) => {
    if (campaign.triggerType === 'AccountDepositTrigger') {
      trackSignUpDepositClick(campaign?.isLive ?? false)
      appHistory.push('/')
      window.setTimeout(openDeposit, 100)
    } else {
      appHistory.push(getCampaignRaceUrl(campaign))
    }
  }

  return (
    <SignUpPageStyled>
      <AccountHeader
        alignItems='start'
        title='🎉 Welcome to TABtouch!'
        description='Your account information will be emailed to you.'
        style={{ marginBottom: '0rem' }}
      />
      {isCampaignActive && (
        <>
          <BonusCardContainer>
            <BonusCardHeader>
              <Icon name='LineGift01' size={spacing.md} />
              <BonusCardTitle>{getTitleText(campaign)}</BonusCardTitle>
              <BonusCardExpiry>{getExpiryDate(campaign)}</BonusCardExpiry>
            </BonusCardHeader>

            <BonusCardBody>
              <BonusCardDescription>{campaign.description}</BonusCardDescription>
              {shouldDisplayAmounts(campaign) && (
                <div>
                  <BonusCardTotal>Total: {getTotalAmount(campaign)}</BonusCardTotal>
                  <BonusCardRemaining>{getRemainingAmount(campaign)} Remaining</BonusCardRemaining>
                </div>
              )}
            </BonusCardBody>
          </BonusCardContainer>
          <CampaignDescriptionStyled>
            {getTermsAndConditions(campaign)}
            <a
              href='https://help.tabtouch.com.au/knowledge-base/bonus-offers-terms-conditions/'
              target='_blank'
              rel='noreferrer'
            >
              Terms and Conditions.
            </a>
          </CampaignDescriptionStyled>
        </>
      )}
      <Footer>
        {campaign && shouldShowButton(campaign) && (
          <Button
            color='primary'
            isFullWidth
            size='md'
            onClick={() => handleCampaignClick(campaign)}
          >
            {getButtonText(campaign)}
          </Button>
        )}
        {!campaign && (
          <Button
            color='primary'
            isFullWidth
            size='md'
            onClick={() => {
              trackSignUpDepositClick(false)
              appHistory.push(DEPOSIT_ROOT)
            }}
          >
            Deposit now to start betting
          </Button>
        )}
        <Button
          color={isCampaignActive ? 'tertiary_color' : 'secondary_grey'}
          isFullWidth
          size='md'
          onClick={() => {
            trackSignUpGoBackHomePage(location.pathname, true)
            appHistory.push('/')
          }}
        >
          {isCampaignActive ? "I'll do it later" : 'Home'}
        </Button>
      </Footer>

      <Lottie
        options={{
          autoplay: true,
          loop: false,
          animationData: LottieConfetti,
          rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
        }}
        style={{
          position: 'absolute',
          zIndex: 3,
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          pointerEvents: 'none',
        }}
      />
    </SignUpPageStyled>
  )
}
